import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Address } from '../../../utils/checkoutState';
import { config } from '../../../utils/config';
import Button from '../../atoms/Button';
import FormattedAddress from '../../atoms/FormattedAddress';
import LoadingDots from '../../atoms/LoadingDots';
import Paragraph from '../../atoms/Paragraph';
import SubHeadline from '../../atoms/SubHeadline';
import './style.scss';

type AddressListProps = {
  onCancel: () => void;
  context: 'shippingAddress' | 'billingAddress';
  addresses?: Address[];
  onSelect: (address: Address) => void;
  usage: 'stored' | 'suggestion';
  isLoading?: boolean;
  onDelete?: (id: number) => void;
};

const AddressList = (props: AddressListProps) => {
  const { addresses, context, onCancel, onDelete, onSelect, usage, isLoading } = props;
  const [selectedAddressId, setSelectedAddressId] = useState<number>();
  const { formatMessage } = useIntl();

  const showEmptyState = !addresses?.length && !isLoading;

  const handleClick = () => onSelect(addresses!.find(({ id }) => id === selectedAddressId)!);

  const indexOfLastAddress = addresses ? addresses.length - 1 : 0;

  const hasSoftAddressCheck = usage === 'suggestion' && config.features.addressSuggestions.mode === 'soft';
  const filteredAddressList =
    usage === 'suggestion' ? addresses?.filter((_, index) => index !== indexOfLastAddress) : addresses;

  return (
    <div className="address-book">
      <SubHeadline>
        <FormattedMessage id="address.choose" values={{ addressType: formatMessage({ id: `address.${context}` }) }} />
      </SubHeadline>
      <div
        className={classNames('address-book__list', {
          'address-book__list--centered': isLoading || !addresses?.length,
        })}
      >
        {showEmptyState && (
          <Paragraph>
            <FormattedMessage id="address.noAddresses" />
          </Paragraph>
        )}
        {isLoading && <LoadingDots />}
        {filteredAddressList?.map((address, index) => (
          <div
            key={address.hash}
            className={classNames(
              'address-book__address',
              address.id === selectedAddressId && 'address-book__address--selected'
            )}
            onClick={() => setSelectedAddressId(address.id)}
          >
            {onDelete && (
              <Button
                type="button"
                className="address-book__delete-btn"
                onClick={e => {
                  e.stopPropagation();
                  onDelete?.(address.id);
                }}
              >
                <FormattedMessage id="generic.delete" />
              </Button>
            )}
            {usage === 'suggestion' && index === 0 && (
              <div className="address-book__recommended">
                <FormattedMessage id="address.recommended" />
              </div>
            )}
            {address && <FormattedAddress address={address} />}
          </div>
        ))}
      </div>
      {addresses && hasSoftAddressCheck && (
        <>
          <SubHeadline style={{ marginTop: 16 }}>
            <FormattedMessage id="address.current" />
          </SubHeadline>
          <div className="address-book__list">
            <div
              key={addresses[indexOfLastAddress].hash}
              className={classNames(
                'address-book__address',
                addresses[indexOfLastAddress].id === selectedAddressId && 'address-book__address--selected'
              )}
              onClick={() => setSelectedAddressId(addresses[indexOfLastAddress].id)}
            >
              <FormattedAddress address={addresses[indexOfLastAddress]!} />
            </div>
          </div>
        </>
      )}
      <div className="address-book__button-container">
        <Button secondary type="button" onClick={onCancel}>
          <FormattedMessage id="generic.cancel" />
        </Button>
        <Button
          type="button"
          onClick={handleClick}
          isLoading={isLoading || !addresses}
          disabled={selectedAddressId === undefined}
        >
          <FormattedMessage id="generic.select" />
        </Button>
      </div>
    </div>
  );
};

export default AddressList;
